import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, Eye, ShoppingCart, DollarSign, TrendingUp, 
  Activity, Package, RefreshCw, ArrowUpRight, ArrowDownRight,
  Globe, Smartphone, Monitor, Tablet, Chrome, MapPin,
  MousePointer, Route, BarChart3, Filter
} from 'lucide-react';

function AdminAnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [trafficSources, setTrafficSources] = useState(null);
  const [devices, setDevices] = useState(null);
  const [journeys, setJourneys] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const token = localStorage.getItem('admin_token');

  const fetchAnalytics = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      
      const [overviewRes, realtimeRes, trafficRes, devicesRes, journeysRes, funnelRes] = await Promise.all([
        fetch(`${baseUrl}/api/analytics/admin/overview`, { headers }),
        fetch(`${baseUrl}/api/analytics/admin/realtime`, { headers }),
        fetch(`${baseUrl}/api/analytics/admin/traffic-sources`, { headers }),
        fetch(`${baseUrl}/api/analytics/admin/devices`, { headers }),
        fetch(`${baseUrl}/api/analytics/admin/user-journeys`, { headers }),
        fetch(`${baseUrl}/api/analytics/admin/funnel`, { headers }),
      ]);
      
      setAnalytics(await overviewRes.json());
      setRealtime(await realtimeRes.json());
      setTrafficSources(await trafficRes.json());
      setDevices(await devicesRes.json());
      setJourneys(await journeysRes.json());
      setFunnel(await funnelRes.json());
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const StatCard = ({ title, value, icon: Icon, change, changeLabel, color = "blue", subtitle }) => (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          {change !== undefined && (
            <div className={`flex items-center mt-2 text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span>{Math.abs(change)}% {changeLabel}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </Card>
  );

  const getDeviceIcon = (type) => {
    switch(type?.toLowerCase()) {
      case 'mobile': return Smartphone;
      case 'tablet': return Tablet;
      default: return Monitor;
    }
  };

  const getSourceColor = (source) => {
    const colors = {
      'facebook': 'bg-blue-500',
      'instagram': 'bg-pink-500',
      'google': 'bg-red-500',
      'direct': 'bg-gray-500',
      'whatsapp': 'bg-green-500',
    };
    return colors[source?.toLowerCase()] || 'bg-purple-500';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </AdminLayout>
    );
  }

  const today = analytics?.today || {};
  const yesterday = analytics?.yesterday || {};
  const week = analytics?.week || {};
  const funnelData = funnel?.funnel || [];
  const funnelMetrics = funnel?.metrics || {};

  const visitorsChange = yesterday.visitors > 0 
    ? Math.round(((today.visitors - yesterday.visitors) / yesterday.visitors) * 100) 
    : 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
            <p className="text-muted-foreground">Comprehensive visitor tracking & insights</p>
          </div>
          <Button variant="outline" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Live Users Section */}
        <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold">Live Right Now</h2>
              <Badge variant="outline" className="bg-green-100 text-green-700 animate-pulse">
                Live
              </Badge>
            </div>
            <span className="text-sm text-muted-foreground">Updates every 30s</span>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Active Users</p>
              <p className="text-4xl font-bold text-green-600">{realtime?.active_users || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Page Views (30 min)</p>
              <p className="text-4xl font-bold text-green-600">{realtime?.pageviews_30min || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Sessions</p>
              <p className="text-4xl font-bold text-green-600">{realtime?.active_sessions || 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Top Page</p>
              <p className="text-lg font-medium text-green-700 truncate">
                {realtime?.top_pages?.[0]?.page || 'No data'}
              </p>
            </div>
          </div>

          {/* Live Users List */}
          {realtime?.active_users_list?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <p className="text-sm font-medium text-green-800 mb-2">Currently Browsing:</p>
              <div className="flex flex-wrap gap-2">
                {realtime.active_users_list.map((user, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-white/80">
                    <span className="relative flex h-2 w-2 mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    {user.current_page || '/'}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Tabs for different views */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 w-full max-w-2xl">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="traffic">Traffic Sources</TabsTrigger>
            <TabsTrigger value="devices">Devices</TabsTrigger>
            <TabsTrigger value="funnel">Funnel</TabsTrigger>
            <TabsTrigger value="journeys">User Journeys</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            {/* Today's Stats */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Today's Performance</h2>
              <div className="grid md:grid-cols-4 gap-4">
                <StatCard 
                  title="Visitors" 
                  value={today.visitors || 0} 
                  icon={Users}
                  change={visitorsChange}
                  changeLabel="vs yesterday"
                  color="blue"
                />
                <StatCard 
                  title="Page Views" 
                  value={today.pageviews || 0} 
                  icon={Eye}
                  color="purple"
                />
                <StatCard 
                  title="Add to Cart" 
                  value={today.add_to_cart || 0} 
                  icon={ShoppingCart}
                  color="orange"
                />
                <StatCard 
                  title="Orders" 
                  value={today.orders || 0} 
                  icon={Package}
                  color="green"
                />
              </div>
            </div>

            {/* Weekly Stats */}
            <div>
              <h2 className="text-xl font-semibold mb-4">This Week</h2>
              <div className="grid md:grid-cols-4 gap-4">
                <StatCard 
                  title="Total Visitors" 
                  value={week.total_visitors || 0} 
                  icon={Users}
                  subtitle="Unique visitors"
                  color="blue"
                />
                <StatCard 
                  title="Total Orders" 
                  value={week.total_orders || 0} 
                  icon={Package}
                  color="green"
                />
                <StatCard 
                  title="Revenue" 
                  value={`₹${(week.total_revenue || 0).toLocaleString()}`} 
                  icon={DollarSign}
                  color="yellow"
                />
                <StatCard 
                  title="Conversion Rate" 
                  value={`${week.conversion_rate || 0}%`} 
                  icon={TrendingUp}
                  subtitle={`Cart Rate: ${week.cart_rate || 0}%`}
                  color="purple"
                />
              </div>
            </div>

            {/* Catalog & Lifetime Stats */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Store Overview</h2>
              <div className="grid md:grid-cols-5 gap-4">
                <StatCard 
                  title="Total Products" 
                  value={analytics?.catalog_stats?.total_products || 0} 
                  icon={Package}
                  subtitle="In catalog"
                  color="cyan"
                />
                <StatCard 
                  title="Categories" 
                  value={analytics?.catalog_stats?.total_categories || 0} 
                  icon={BarChart3}
                  color="indigo"
                />
                <StatCard 
                  title="Active Products" 
                  value={analytics?.catalog_stats?.active_products || 0} 
                  icon={Activity}
                  subtitle="In stock"
                  color="green"
                />
                <StatCard 
                  title="All-Time Orders" 
                  value={analytics?.lifetime_stats?.total_orders || 0} 
                  icon={ShoppingCart}
                  subtitle="Total orders"
                  color="orange"
                />
                <StatCard 
                  title="All-Time Revenue" 
                  value={`₹${(analytics?.lifetime_stats?.total_revenue || 0).toLocaleString()}`} 
                  icon={DollarSign}
                  subtitle="Total revenue"
                  color="emerald"
                />
              </div>
            </div>

            {/* Top Products */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Top Viewed Products (This Week)</h2>
              {analytics?.top_products?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_products.map((product, index) => (
                    <div key={product.product_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-gray-400">#{index + 1}</span>
                        <span className="font-medium">{product.product_name}</span>
                      </div>
                      <Badge variant="secondary">{product.views} views</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No product view data yet.</p>
              )}
            </Card>
          </TabsContent>

          {/* TRAFFIC SOURCES TAB */}
          <TabsContent value="traffic" className="space-y-6 mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Traffic Source Breakdown */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <h2 className="text-xl font-semibold">Traffic Sources (7 Days)</h2>
                </div>
                {trafficSources?.breakdown?.length > 0 ? (
                  <div className="space-y-3">
                    {trafficSources.breakdown.map((source, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${getSourceColor(source.source)}`} />
                          <div>
                            <p className="font-medium capitalize">{source.source}</p>
                            <p className="text-xs text-muted-foreground">{source.medium}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{source.pageviews} views</p>
                          <p className="text-xs text-muted-foreground">{source.unique_visitors} visitors</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No traffic source data yet.</p>
                )}
              </Card>

              {/* Traffic Source Summary */}
              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">Source Summary</h2>
                <div className="space-y-4">
                  {trafficSources?.breakdown?.slice(0, 5).map((source, idx) => {
                    const total = trafficSources.breakdown.reduce((sum, s) => sum + s.pageviews, 0);
                    const percent = total > 0 ? Math.round((source.pageviews / total) * 100) : 0;
                    return (
                      <div key={idx}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium capitalize">{source.source}</span>
                          <span className="text-sm text-muted-foreground">{percent}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getSourceColor(source.source)}`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>

            {/* Today's Sources */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Today's Traffic Sources</h2>
              {trafficSources?.today?.length > 0 ? (
                <div className="grid md:grid-cols-3 gap-4">
                  {trafficSources.today.map((source, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-2 h-2 rounded-full ${getSourceColor(source.source)}`} />
                        <span className="font-medium capitalize">{source.source}</span>
                      </div>
                      <p className="text-2xl font-bold">{source.visits}</p>
                      <p className="text-xs text-muted-foreground">{source.medium}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No traffic data for today yet.</p>
              )}
            </Card>
          </TabsContent>

          {/* DEVICES TAB */}
          <TabsContent value="devices" className="space-y-6 mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Device Types */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Smartphone className="w-5 h-5 text-purple-600" />
                  <h2 className="text-xl font-semibold">Device Types</h2>
                </div>
                {devices?.device_types?.length > 0 ? (
                  <div className="space-y-3">
                    {devices.device_types.map((device, idx) => {
                      const Icon = getDeviceIcon(device.type);
                      const total = devices.device_types.reduce((sum, d) => sum + d.count, 0);
                      const percent = total > 0 ? Math.round((device.count / total) * 100) : 0;
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5 text-gray-600" />
                            <span className="font-medium capitalize">{device.type}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">{percent}%</span>
                            <Badge variant="secondary">{device.count}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No device data yet.</p>
                )}
              </Card>

              {/* Browsers */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Chrome className="w-5 h-5 text-blue-600" />
                  <h2 className="text-xl font-semibold">Browsers</h2>
                </div>
                {devices?.browsers?.length > 0 ? (
                  <div className="space-y-3">
                    {devices.browsers.map((browser, idx) => {
                      const total = devices.browsers.reduce((sum, b) => sum + b.count, 0);
                      const percent = total > 0 ? Math.round((browser.count / total) * 100) : 0;
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium capitalize">{browser.browser}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">{percent}%</span>
                            <Badge variant="secondary">{browser.count}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No browser data yet.</p>
                )}
              </Card>

              {/* Operating Systems */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Monitor className="w-5 h-5 text-green-600" />
                  <h2 className="text-xl font-semibold">Operating Systems</h2>
                </div>
                {devices?.operating_systems?.length > 0 ? (
                  <div className="space-y-3">
                    {devices.operating_systems.map((os, idx) => {
                      const total = devices.operating_systems.reduce((sum, o) => sum + o.count, 0);
                      const percent = total > 0 ? Math.round((os.count / total) * 100) : 0;
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium capitalize">{os.os}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">{percent}%</span>
                            <Badge variant="secondary">{os.count}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No OS data yet.</p>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* FUNNEL TAB */}
          <TabsContent value="funnel" className="space-y-6 mt-6">
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <Filter className="w-5 h-5 text-orange-600" />
                <h2 className="text-xl font-semibold">Conversion Funnel (Last 7 Days)</h2>
              </div>
              
              {/* Funnel Visualization */}
              <div className="space-y-4 mb-8">
                {funnelData.map((stage, idx) => {
                  const maxCount = funnelData[0]?.count || 1;
                  const width = Math.max(20, (stage.count / maxCount) * 100);
                  const dropoff = idx > 0 ? funnelData[idx - 1].count - stage.count : 0;
                  
                  return (
                    <div key={stage.stage} className="relative">
                      <div className="flex items-center gap-4">
                        <div className="w-28 text-sm font-medium">{stage.stage}</div>
                        <div className="flex-1">
                          <div 
                            className="h-10 bg-gradient-to-r from-blue-500 to-blue-400 rounded-lg flex items-center justify-end pr-4 transition-all"
                            style={{ width: `${width}%` }}
                          >
                            <span className="text-white font-bold">{stage.count.toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="w-20 text-right">
                          {idx > 0 && dropoff > 0 && (
                            <span className="text-red-500 text-sm">-{dropoff}</span>
                          )}
                        </div>
                      </div>
                      {idx > 0 && (
                        <div className="ml-28 pl-4 text-xs text-muted-foreground">
                          {stage.rate}% of previous stage
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Funnel Metrics */}
              <div className="grid md:grid-cols-4 gap-4 pt-6 border-t">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Add to Cart Rate</p>
                  <p className="text-2xl font-bold text-blue-600">{funnelMetrics.add_to_cart_rate || 0}%</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Checkout Rate</p>
                  <p className="text-2xl font-bold text-purple-600">{funnelMetrics.checkout_rate || 0}%</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Purchase Rate</p>
                  <p className="text-2xl font-bold text-green-600">{funnelMetrics.purchase_rate || 0}%</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Avg Order Value</p>
                  <p className="text-2xl font-bold text-orange-600">₹{(funnelMetrics.average_order_value || 0).toLocaleString()}</p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* USER JOURNEYS TAB */}
          <TabsContent value="journeys" className="space-y-6 mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Common Paths */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Route className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-xl font-semibold">Common User Paths (Today)</h2>
                </div>
                {journeys?.common_paths?.length > 0 ? (
                  <div className="space-y-3">
                    {journeys.common_paths.map((path, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="secondary">{path.count} users</Badge>
                        </div>
                        <p className="text-sm text-gray-700 font-mono">{path.path}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No path data yet. Paths show first 3 pages per session.</p>
                )}
              </Card>

              {/* Recent Sessions */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MousePointer className="w-5 h-5 text-pink-600" />
                  <h2 className="text-xl font-semibold">Recent Sessions (Today)</h2>
                </div>
                {journeys?.recent_sessions?.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {journeys.recent_sessions.slice(0, 10).map((session, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {session.device_type === 'mobile' ? (
                              <Smartphone className="w-4 h-4 text-gray-500" />
                            ) : (
                              <Monitor className="w-4 h-4 text-gray-500" />
                            )}
                            <span className="font-medium capitalize">{session.traffic_source || 'direct'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {session.has_add_to_cart && (
                              <Badge className="bg-orange-100 text-orange-700 text-xs">Cart</Badge>
                            )}
                            {session.has_purchase && (
                              <Badge className="bg-green-100 text-green-700 text-xs">Purchased</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <p>Landing: {session.landing_page || '/'}</p>
                          <p>{session.pages?.length || 0} pages viewed</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No session data yet.</p>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Daily Trend */}
        {week.daily_data?.length > 0 && (
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Daily Trend (Last 7 Days)</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Date</th>
                    <th className="text-right py-2 px-3">Visitors</th>
                    <th className="text-right py-2 px-3">Page Views</th>
                    <th className="text-right py-2 px-3">Add to Cart</th>
                    <th className="text-right py-2 px-3">Orders</th>
                    <th className="text-right py-2 px-3">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {week.daily_data.map((day) => (
                    <tr key={day.date} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3">{day.date}</td>
                      <td className="text-right py-2 px-3">{(day.unique_visitors || day.unique_sessions)?.length || 0}</td>
                      <td className="text-right py-2 px-3">{day.pageviews || 0}</td>
                      <td className="text-right py-2 px-3">{day.add_to_cart_count || 0}</td>
                      <td className="text-right py-2 px-3">{day.purchase_count || 0}</td>
                      <td className="text-right py-2 px-3">₹{(day.revenue || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminAnalyticsPage;
